import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import CheckRequirements from '@/components/CheckRequirements';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import PRODUCTS from '@/constants/products.constants';
import useDraftState from '@/hooks/useDraftState';
import useProductsCheck from '@/hooks/useProductsCheck';
import type { SettingsTypes } from '@/pages/Administration/Administration.types';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';
import { userIsInternalAtom } from '@/store/user.store';

import type { ManagedEDRProps } from './ManagedEDR.types';

const ManagedEDR = (props: ManagedEDRProps) => {
  const { classes } = useStyles();
  const { handleSave, orgInfo } = props;
  const [editing, isEditing] = useState<boolean>(false);
  const [updatedOrgInfo, updateDraft, resetDraft] = useDraftState(orgInfo);
  const userIsInternal = useAtomValue(userIsInternalAtom);

  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const changedField = event.target.name as keyof SettingsTypes;
    updateDraft({ [changedField]: event.target.checked });
  };

  const hasEdr = useProductsCheck([PRODUCTS.EDR]);

  return (
    <LargeCard
      title="Managed EDR"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  handleSave(updatedOrgInfo);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  resetDraft();
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => isEditing(!editing)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      {(hasEdr || userIsInternal || updatedOrgInfo.display_host_isolation) && (
        <>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Host Isolation
          </Typography>
          <Typography className={classes.subText} variant="body2">
            When enabled, the Pondurance SOC is authorized to initiate host
            isolation actions prior to communication with the customer during an
            escalation-worthy event.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={updatedOrgInfo.actions_host_isolation_enabled || false}
                onChange={handleEnabledChange}
                disabled={!editing}
                color="primary"
                name="actions_host_isolation_enabled"
              />
            }
            label="Enable submission for host isolation"
          />
        </>
      )}
      <CheckRequirements products={[PRODUCTS.EDR]}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Verdict & Case Notes Update
        </Typography>
        <Typography className={classes.subText} variant="body2">
          When enabled, the Pondurance SOC will be able to update your EDR
          console with a Verdict and Case Notes for the Threat (S1), Detection
          (CrowdStrike), or Alert (MS Defender) events worked by our SOC and
          automation.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={
                updatedOrgInfo.actions_verdict_case_notes_update_enabled ||
                false
              }
              onChange={handleEnabledChange}
              disabled={!editing}
              color="primary"
              name="actions_verdict_case_notes_update_enabled"
            />
          }
          label="Enable verdict & case notes update"
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Change Status in EDR Console
        </Typography>
        <Typography className={classes.subText} variant="body2">
          When enabled, Pondurance SOC will be able to change the status of the
          Threat(S1), Detection(CrowdStrike), or Alert(MS Defender) in your EDR
          console for events worked by our SOC and automation.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={updatedOrgInfo.actions_change_status_enabled || false}
              onChange={handleEnabledChange}
              disabled={!editing}
              color="primary"
              name="actions_change_status_enabled"
            />
          }
          label="Enable change status in EDR console"
        />
        <Divider sx={{ my: 2 }} />
      </CheckRequirements>
      {!hasEdr && (
        <CheckRequirements permissions={[PERMISSIONS.INTERNAL]}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Show Customer Host Isolation Option
          </Typography>
          <Typography className={classes.subText} variant="body2">
            When enabled, the customer will be able to see the host isolation
            toggle, even without any EDR products.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={updatedOrgInfo.display_host_isolation || false}
                onChange={handleEnabledChange}
                disabled={!editing}
                color="primary"
                name="display_host_isolation"
              />
            }
            label="Enable host isolation toggle without EDR products"
          />
        </CheckRequirements>
      )}
    </LargeCard>
  );
};

export default ManagedEDR;
